import { ALLOCATION_SKILLS } from "@/constants/allocations";

export type AllocationSkillId = keyof typeof ALLOCATION_SKILLS;

export type AllocationSkills = {
  [K in AllocationSkillId]?: number;
};

export interface WorkerAllocation {
  userId: number;
  name: string;
  departmentId: string;
  department: string;
  shiftStart: string;
  shiftEnd: string;
  allocationSkills: AllocationSkills;
  receivingLeads: boolean;
}

export interface AllocationsUploadResponse {
  success: boolean;
  message?: string;
  errors?: string[];
  processedCount?: number;
  failedCount?: number;
}

export interface AllocationsFilter {
  department: string;
  search: string;
}

export interface AllocationSkillResponse {
  userId: number;
  receivingLeads: boolean;
  shiftStart: string | null;
  shiftEnd: string | null;
  allocationSkills: AllocationSkills;
}

export interface ShiftCapsPayload {
  userId: number;
  shiftStart: string | null;
  shiftEnd: string | null;
  allocationSkills: AllocationSkills;
}

export interface ValidationErrorResponse {
  [key: string]: string;
}

export interface ValidationError {
  type: "validation";
  errors: ValidationErrorResponse;
}

export const isValidationError = (err: unknown): err is ValidationError =>
  typeof err === "object" && err !== null && "type" in err && (err as ValidationError).type === "validation";

export interface ShiftCapsSubmitResponse {
  message: string;
}

export interface AvailableSkill {
  allocationSkillId: number;
  allocationSkillTypeId: number;
  name: string;
  value: string;
}

export interface UserSkillAssignment {
  intranetUserId: number;
  allocationSkillId: number;
  isPrimary: boolean;
  allocationSkillTypeId: number;
  numberOfAllocations: number;
  cap: number | null;
}

export interface UpdateUserSkillsPayload {
  userId: number;
  primarySkillIds: number[];
  secondarySkillIds: number[];
  originalTarget: number;
  newTarget: number;
}

export interface CapFilter {
  skillId: AllocationSkillId;
  min?: number;
  max?: number;
}

export type SkillFilterMode = "any" | "all";

export interface AllocationLeadSourceStats {
  sourceType: number;
  cap: number;
  isOnOrOverCap: boolean;
  isUnderCap: boolean;
  overCapCount: number;
  allocatedTodayCount: number;
  hasRecentAllocation: boolean;
  lastAllocatedDate: string;
  agentParticipationCount: number;
  agentParticipationRatio: number;
  allocatedCount: number;
  paidOutCount: number;
  conversion: number;
}

export interface AllocationAgentSummary {
  agentId: number;
  agentEmail: string;
  agentName: string;
  agentDepartmentId: string;
  leadSource: AllocationLeadSourceStats;
  allocatedCount: number;
  paidOutCount: number;
  conversion: number;
  hasRecentAllocation: boolean;
}

export interface AllocationParticipatingAgent {
  agentId: number;
  financeExecutiveId: number;
  name: string;
  email: string;
  departmentName: string | null;
  departmentId: string;
}

export interface AllocationReportRow {
  agentId: number;
  applicationId: number;
  runDate: string;
  allocated: boolean;
  reason: string;
  detail: string | null;
  isWorking: boolean | null;
  rankPosition: number | null;
  recentCallbacks: number | null;
  recentLeads: number | null;
  percentOfDynamicTargetAllocated: number | null;
  dynamicTarget: number | null;
  rankedCutAt: number | null;
  allocatedCount: number | null;
  paidOutCount: number | null;
  conversion: number | null;
  allocatedByLeadSourceCount: number | null;
  paidOutByLeadSourceCount: number | null;
  conversionBySourceCount: number | null;
  version: string | null;
}

export interface AllocationOutcomeResponse {
  shiftDate: string;
  requiredSkills: string[];
  topRankedAgent: AllocationAgentSummary | null;
  matchedAgents: AllocationAgentSummary[];
  participatingAgents: AllocationParticipatingAgent[];
  allocationLog: AllocationReportRow[];
}
